"use client";

import { type FC, type Ref, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { OcktoCard } from "@huub-web/ui";

import { usePageContent } from "../../hooks/usePageContent";
import { useTemplates } from "../../hooks";
import { OcktoModal } from "./components/OcktoModal";

export type OcktoCardTemplateProps = {
  /**
   * Forwarded ref for the root div element
   */
  ref?: Ref<HTMLDivElement>;
  /**
   * Unique identifier for the card
   */
  id: string;
  /**
   * Card title text
   */
  title: string;
  /**
   * Card description text
   */
  description: string;
  /**
   * CTA button label (defaults to translated "Gegevens ophalen")
   */
  buttonValue?: string;
  /**
   * API endpoint for QR code fetching
   */
  endpoint: string;
  /**
   * Modal header text (defaults to title if not provided)
   */
  modalTitle?: string;
  /**
   * Modal body description text (defaults to description if not provided)
   */
  modalDescription?: string;
  /**
   * Modal info box message text (defaults to empty string if not provided)
   */
  modalInfoText?: string;
  /**
   * Whether the card is disabled (honored for rollout safety)
   */
  disabled?: boolean;
  /**
   * Passed through to OcktoCard for border styling only (animated gradient when true)
   */
  active?: boolean;
};

/**
 * Template that renders a carousel-compatible card for Ockto data import.
 * Delegates to OcktoCard (packages/ui) which handles ContentMenu registration
 * as a leading item with a QR icon. Clicking the CTA opens a modal with
 * QR code and instructions.
 */
export const OcktoCardTemplate: FC<OcktoCardTemplateProps> = ({
  ref,
  id,
  title,
  description,
  buttonValue,
  endpoint,
  modalTitle,
  modalDescription,
  modalInfoText,
  disabled,
  active,
}) => {
  const { t } = useTranslation();
  const { lockUI } = useTemplates();
  const [modalOpen, setModalOpen] = useState(false);

  const isDisabled = disabled || lockUI;

  const contentItem = useMemo(
    () => ({
      type: "card" as const,
      label: title,
      description,
    }),
    [title, description],
  );
  usePageContent("ockto", contentItem);

  const resolvedButtonValue = buttonValue || t("ocktoCard.defaultButtonValue");
  const resolvedModalTitle = modalTitle || title;
  const resolvedModalDescription = modalDescription || description;
  const resolvedModalInfoText = modalInfoText || "";

  const handleOpen = useCallback(() => {
    setModalOpen(true);
  }, []);

  return (
    <>
      <OcktoCard
        ref={ref}
        id={id}
        title={title}
        description={description}
        buttonValue={resolvedButtonValue}
        label={title}
        onClick={handleOpen}
        disabled={isDisabled}
        active={active}
      />
      {modalOpen && (
        <OcktoModal
          endpoint={endpoint}
          modalTitle={resolvedModalTitle}
          modalDescription={resolvedModalDescription}
          modalInfoText={resolvedModalInfoText}
          lockUI={lockUI}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
};
